import { useCallback, useEffect, useState } from 'react';
import {
  createBanner,
  deleteBanner,
  extractBannerFromResponse,
  extractBannersFromResponse,
  fetchBanners,
  getBannerImageUrl,
  updateBanner,
} from '../api/banners';
import { getApiErrorMessage } from '../api/errors';
import AdminIconButton from './AdminIconButton';
import AdminModal from './AdminModal';
import AdminPagination from './AdminPagination';
import AdminTableLoader from './AdminTableLoader';
import { confirmDelete, showError, showSuccess } from './adminAlerts';
import { usePagination } from './usePagination';
import './adminShared.css';

function AdminBanner() {
  const [banners, setBanners] = useState([]);
  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const loadBanners = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetchBanners();
      setBanners(extractBannersFromResponse(response));
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not load banners.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setTitle('');
    setImageFile(null);
    setExistingImageUrl('');
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingId(null);
    setTitle('');
    setImageFile(null);
    setExistingImageUrl('');
  };

  const openEditModal = (banner) => {
    setModalMode('edit');
    setEditingId(banner.id);
    setTitle(banner.title);
    setImageFile(null);
    setExistingImageUrl(getBannerImageUrl(banner));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (modalMode === 'add' && !imageFile) {
      showError('Choose an image to upload.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (modalMode === 'add') {
        const response = await createBanner({ title: title.trim(), imageFile });
        const banner = extractBannerFromResponse(response);
        setBanners((prev) => [banner, ...prev]);
        showSuccess('Banner created.');
      } else {
        const response = await updateBanner(editingId, {
          title: title.trim(),
          imageFile,
        });
        const updated = extractBannerFromResponse(response);
        setBanners((prev) =>
          prev.map((item) => (item.id === editingId ? { ...item, ...updated } : item))
        );
        showSuccess('Banner updated.');
      }
      closeModal();
    } catch (err) {
      showError(
        getApiErrorMessage(
          err,
          modalMode === 'add' ? 'Could not create banner.' : 'Could not update banner.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete({
      title: 'Delete banner?',
      text: 'This banner will be removed permanently.',
    });
    if (!confirmed) return;

    setBusyId(id);

    try {
      await deleteBanner(id);
      setBanners((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) closeModal();
      showSuccess('Banner deleted.');
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not delete banner.'));
    } finally {
      setBusyId(null);
    }
  };

  const displayPreview = previewUrl || existingImageUrl;

  const {
    paginatedItems,
    page,
    setPage,
    totalPages,
    totalItems,
    rangeStart,
    rangeEnd,
  } = usePagination(banners);

  return (
    <div className="admin-page">
      <div className="admin-page__top">
        <div>
          <h1 className="admin-page__title">Banner</h1>
          <p className="admin-page__subtitle">Create and manage carousel banners.</p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openAddModal}>
          + Add
        </button>
      </div>

      {isLoading ? (
        <AdminTableLoader
          headers={['ID', 'Image', 'Title', 'Actions']}
          columns={['id', 'thumb', 'title', 'actions']}
        />
      ) : banners.length === 0 ? (
        <p className="admin-panel__note">No banners yet.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Image</th>
                <th>Title</th>
                <th className="admin-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((banner, index) => {
                const imageUrl = getBannerImageUrl(banner);
                return (
                  <tr key={banner.id ?? `banner-${index}`}>
                    <td className="admin-table__id">{banner.id}</td>
                    <td>
                      {imageUrl ? (
                        <img src={imageUrl} alt="" className="admin-table__thumb" />
                      ) : (
                        <span className="admin-table__thumb--empty" aria-hidden="true" />
                      )}
                    </td>
                    <td>
                      <span className="admin-table__title">{banner.title}</span>
                    </td>
                    <td className="admin-table__actions">
                      <AdminIconButton
                        label="Edit banner"
                        onClick={() => openEditModal(banner)}
                        disabled={busyId === banner.id}
                      />
                      <AdminIconButton
                        label="Delete banner"
                        variant="danger"
                        onClick={() => handleDelete(banner.id)}
                        disabled={busyId === banner.id}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <AdminPagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onPageChange={setPage}
          />
        </div>
      )}

      {modalMode && (
        <AdminModal
          title={modalMode === 'add' ? 'Add banner' : 'Edit banner'}
          onClose={closeModal}
        >
          <form className="admin-form-grid" onSubmit={handleSubmit}>
            <label className="admin-field">
              <span className="admin-field__label">Title</span>
              <input
                className="admin-field__input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </label>
            <label className="admin-field">
              <span className="admin-field__label">
                {modalMode === 'add' ? 'Image' : 'Replace image (optional)'}
              </span>
              <input
                type="file"
                className="admin-field__input admin-field__file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                required={modalMode === 'add'}
                disabled={isSubmitting}
              />
            </label>
            {displayPreview && (
              <img
                src={displayPreview}
                alt=""
                className="admin-preview-img--banner"
              />
            )}
            <div className="admin-actions">
              <button
                type="submit"
                className="admin-btn admin-btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Save'}
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </AdminModal>
      )}
    </div>
  );
}

export default AdminBanner;
